import React, { useState, useRef, useEffect, useCallback } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Tag from "../Tag";
import IconAndTextButton from "../Buttons/IconAndTextButton";
import closeIcon from "../../../assets/close__s__stroke.svg";
import "./multiselectdd.css";

const MultiSelectDropdown = ({
  selectValues,
  values = [],
  handleOnChange,
  dropdownLabel,
  disabled,
  clsNames,
  enableAutocomplete = true,
  tagsWithDelete = true,
  tagClassName = "mas-tag-multi-select mas-tag-max-wd",
  closeIconSrc = closeIcon,
  placement = "bottom",
  onSearchNoResults,
  clearAllButton = {
    text: "Clear all",
    clsNames: "clear-all-btn",
    custStyles: {},
    textStyles: {},
  },
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animationState, setAnimationState] = useState("closed");
  const [selectedValues, setSelectedValues] = useState(disabled ? [] : values);
  const [searchTerm, setSearchTerm] = useState(""); // For autocomplete
  const [highlightedIndex, setHighlightedIndex] = useState(-1); // Track highlighted option for keyboard navigation
  const [isSearchMode, setIsSearchMode] = useState(false); // Track if user is in search mode
  const prevValuesRef = useRef(values);

  const dropdownRef = useRef(null);
  const headerInputRef = useRef(null); // Ref for the header input (for autocomplete)

  // Update selected values when external state changes, but only if they're different
  useEffect(() => {
    // Prevent infinite loop by only updating when values actually change
    if (JSON.stringify(prevValuesRef.current) !== JSON.stringify(values)) {
      prevValuesRef.current = values;
      setSelectedValues(disabled ? [] : values);
    }
  }, [disabled, values]);

  // Define handleClose as a memoized callback to avoid dependency issues
  const handleClose = useCallback(() => {
    if (isOpen) {
      setAnimationState("close");
      setSearchTerm(""); // Clear search when closing
      setHighlightedIndex(-1); // Reset highlighted index when closing
      setIsSearchMode(false); // Exit search mode when closing
      setTimeout(() => {
        setIsOpen(false);
        setAnimationState("closed");
      }, 250); // Match animation duration
    }
  }, [isOpen]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleClose]);
  const handleOptionClick = (value) => {
    let newSelectedValues;

    if (selectedValues.includes(value)) {
      // If already selected, remove it
      newSelectedValues = selectedValues.filter((val) => val !== value);
    } else {
      // If not selected, add it
      newSelectedValues = [...selectedValues, value];
    }

    // Update the local state
    setSelectedValues(newSelectedValues);
    
    // Update prevValuesRef to prevent useEffect from triggering another update
    prevValuesRef.current = newSelectedValues;
    
    // Notify parent component of the change
    handleOnChange(newSelectedValues);
    // Don't close the dropdown when an item is selected
  };
  const handleRemoveTag = (valueToRemove) => {
    const newSelectedValues = selectedValues.filter(
      (value) => value !== valueToRemove
    );
    
    // Update the local state
    setSelectedValues(newSelectedValues);
    
    // Update prevValuesRef to prevent useEffect from triggering another update
    prevValuesRef.current = newSelectedValues;
    
    // Notify parent component of the change
    handleOnChange(newSelectedValues);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    
    // Update the local state
    setSelectedValues([]);
    
    // Update prevValuesRef to prevent useEffect from triggering another update
    prevValuesRef.current = [];
    
    // Notify parent component of the change
    handleOnChange([]);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        setIsOpen(true);
        setAnimationState("open");
        setHighlightedIndex(-1); // Reset highlighted index when opening
        // Focus search input when opening (if autocomplete is enabled)
        if (enableAutocomplete) {
          setTimeout(() => {
            headerInputRef.current?.focus();
          }, 100);
        }
      } else {
        handleClose();
      }
    }
  };

  // Get labels for the selected values
  const getSelectedLabels = () => {
    return selectedValues.map((value) => {
      const option = selectValues.find((opt) => opt.value === value);
      return option ? option.label : value;
    });
  };

  // Filter options based on search term (for autocomplete)
  const filteredOptions = enableAutocomplete && searchTerm
    ? selectValues.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : selectValues;

  // Notify parent component when search yields no results
  useEffect(() => {
    if (onSearchNoResults && enableAutocomplete && searchTerm) {
      const hasNoResults = filteredOptions.length === 0;
      onSearchNoResults(hasNoResults);
    } else if (onSearchNoResults && !searchTerm) {
      // Reset the no results state when search is cleared
      onSearchNoResults(false);
    }
  }, [filteredOptions?.length, searchTerm, onSearchNoResults, enableAutocomplete]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        break;
      default:
        break;
    }
  };

  // Handle entering search mode when user starts typing
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setHighlightedIndex(-1); // Reset highlight when typing
    
    // Enter search mode as soon as user types
    if (!isSearchMode && value.length > 0) {
      setIsSearchMode(true);
    }
    
    // Open dropdown if not already open
    if (!isOpen) {
      setIsOpen(true);
      setAnimationState("open");
    }
  };

  // Handle search input blur
  const handleSearchInputBlur = () => {
    // Exit search mode and clear search when input loses focus
    // Use setTimeout to allow for potential click events to be processed first
    setTimeout(() => {
      if (!isOpen || (isOpen && searchTerm === "")) {
        setIsSearchMode(false);
        setSearchTerm("");
      }
    }, 150);
  };

  // Handle key events to enable typing anywhere to enter search mode
  const handleContainerKeyDown = (e) => {
    // If user starts typing and we have autocomplete enabled, enter search mode
    if (enableAutocomplete && !isSearchMode && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      setIsSearchMode(true);
      setSearchTerm(e.key);
      if (!isOpen) {
        setIsOpen(true);
        setAnimationState("open");
      }
      setTimeout(() => {
        headerInputRef.current?.focus();
      }, 50);
      return;
    }
    
    // Handle other keyboard navigation
    handleKeyDown(e);
  };
  return (
    <div
      ref={dropdownRef}
      className={clsNames || "multi-select-dropdown"}
      data-dropdown
      data-disabled={disabled ? "true" : "false"}
      data-state={isOpen ? "open" : "closed"}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
    >
      <div 
        data-part="header" 
        onClick={!isSearchMode && selectedValues?.length > 0 ? toggleDropdown : undefined}
      >
        <div data-part="selected-tags">
          {isSearchMode ? (
            // Search mode: show search input
            <input
              ref={headerInputRef}
              type="text"
              placeholder={selectedValues?.length > 0 ? "Search for more options..." : (dropdownLabel || "Search options...")}
              value={searchTerm}
              autoComplete="off"
              onChange={handleSearchInputChange}
              onFocus={() => {
                if (!isOpen) {
                  setIsOpen(true);
                  setAnimationState("open");
                }
              }}
              onBlur={handleSearchInputBlur}
              onKeyDown={handleKeyDown}
              onClick={(e) => e?.stopPropagation()}
              disabled={disabled}
              data-part="header-input"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                width: '100%',
                fontSize: 'inherit',
                fontFamily: 'inherit',
                color: 'inherit'
              }}
            />
          ) : selectedValues.length === 0 ? (
            // No selection mode: show placeholder or input for autocomplete
            enableAutocomplete ? (
              <input
                ref={headerInputRef}
                type="text"
                placeholder={dropdownLabel || "Select options..."}
                value=""
                autoComplete="off"
                onChange={handleSearchInputChange}
                onFocus={() => {
                  if (!isOpen) {
                    setIsOpen(true);
                    setAnimationState("open");
                  }
                }}
                onKeyDown={handleKeyDown}
                onClick={(e) => e?.stopPropagation()}
                disabled={disabled}
                data-part="header-input"
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  width: '100%',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  color: 'inherit'
                }}
              />
            ) : (
              <span data-part="placeholder-text">
                {dropdownLabel || "Select options..."}
              </span>
            )
          ) : (
            // Tags mode: show selected tags
            <div className="selected-tags-container">
              {getSelectedLabels()?.map((label, index) => (
                <Tag
                  key={selectedValues[index]}
                  text={label}
                  clsNames={tagClassName}
                  withIconFunc={tagsWithDelete}
                  onClickFnIcon={
                    tagsWithDelete
                      ? (e) => {
                          e.stopPropagation();
                          handleRemoveTag(selectedValues[index]);
                        }
                      : null
                  }
                  iconProps={
                    tagsWithDelete
                      ? {
                          clsNames: "",
                          iconStyles: {},
                          iconPos: "right",
                          icon: closeIconSrc,
                        }
                      : null
                  }
                />
              ))}
              {selectedValues.length > 0 && (
                <IconAndTextButton
                  text={clearAllButton.text}
                  clsNames={clearAllButton.clsNames}
                  custStyles={clearAllButton.custStyles}
                  textStyles={clearAllButton.textStyles}
                  onClickFn={handleClearAll}
                />
              )}
            </div>
          )}
        </div>
        <span data-part="icon">
          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </span>
      </div>

      {/* Keep the options in DOM during closing animation */}
      {(isOpen || animationState === "close") && !disabled && (
        <div 
          data-part="options" 
          data-animate={animationState}
          data-placement={placement}
        >
          {filteredOptions?.length > 0 ? (
            filteredOptions?.map((option, index) => (
              <div
                key={option.value}
                data-part="option"
                data-selected={
                  selectedValues.includes(option.value) ? "true" : "false"
                }
                data-highlighted={highlightedIndex === index ? "true" : "false"}
                onClick={() => handleOptionClick(option.value)}
                onMouseEnter={() => setHighlightedIndex(index)} // Update highlight on hover
              >
                <span>{option.label}</span>
              </div>
            ))
          ) : (
            enableAutocomplete && searchTerm && (
              <div data-part="no-results">
                {searchTerm} is not available
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(MultiSelectDropdown);
